/**
 * Akıllı Arama — kriterleri daire ve emlak portföy kayıtlarıyla eşleştirip puanlar.
 * Saf fonksiyonlar; tüm veri istemcide, dışarı hiçbir şey gönderilmez.
 */

import { AramaKriterleri, trFold, trIcerir } from './daireAramaParser';

export interface EslestirmeDaire {
  daireID: number;
  daireNo: string;
  daireDurumu: string;
  listeFiyati: number | null;
  netM2: number | null;
  brutM2?: number | null;
  proje: { projeID: number; projeAdi: string; projeDurumu?: string; sehir?: string | null } | null;
  blok: { blokID: number; blokAdi: string } | null;
  kat: { katID: number; katAdi: string } | null;
  cephe: { cepheID: number; cepheAdi: string } | null;
  manzara?: { manzaraID: number; manzaraAdi: string } | null;
  daireTipi?: { daireTipID: number; tip: string } | null;
}

export interface EslestirmePortfoy {
  portfoyID: number;
  baslik: string;
  adres: string | null;
  fiyat: number;
  m2: number;
  ilanNo: string;
  tur: string;
  durum: string;
  aciklama?: string | null;
  ozellikler?: { ozellikID: number; ozellikAdi: string }[];
}

export interface EslesmeNedeni {
  metin: string;
  ton: 'iyi' | 'uyari' | 'notr';
}

export interface AramaSonucu {
  tur: 'Daire' | 'Portfoy';
  id: number;
  baslik: string;
  altBaslik: string;
  fiyat: number | null;
  skor: number;
  tamEslesme: boolean;
  nedenler: EslesmeNedeni[];
  daire?: EslestirmeDaire;
  portfoy?: EslestirmePortfoy;
}

const A = {
  fiyat: 40, odaTipi: 30, m2: 15, projeAdi: 15, gayrimenkulTuru: 15,
  yeniProje: 10, sehir: 10, manzara: 5, cephe: 5, kat: 5, serbest: 5,
};

const TAM_ESLESME_ESIGI = 45;

/** Serbest metinden "2+1" gibi oda tipini çıkarır ("3+1 Dubleks" → "3+1"). */
const odaTipiCikar = (tip: string | null | undefined): string | null => {
  if (!tip) return null;
  const m = trFold(tip).match(/(\d{1,2})\s*\+\s*(\d{1,2})/);
  return m ? `${m[1]}+${m[2]}` : null;
};

/** Aralık dışı değerler için doğrusal düşüş: sınırda 1.0, tolerans sonunda 0. */
const araliktanPuan = (deger: number, min: number | null, max: number | null, ustTolerans: number, altTolerans: number) => {
  if (min !== null && max !== null && deger >= min && deger <= max) return 1;
  if (min === null && max !== null && deger <= max) return 1;
  if (max === null && min !== null && deger >= min) return 1;
  if (max !== null && deger > max) {
    const r = (deger - max) / max;
    return Math.max(0, 1 - r / ustTolerans);
  }
  if (min !== null && deger < min) {
    const r = (min - deger) / min;
    return Math.max(0, 1 - r / altTolerans);
  }
  return 0;
};

const yuzde = (r: number) => Math.round(r * 100);

interface Toplayici {
  kazanilan: number;
  toplam: number;
  nedenler: EslesmeNedeni[];
}

const ekle = (t: Toplayici, agirlik: number, oran: number, neden?: EslesmeNedeni) => {
  t.toplam += agirlik;
  t.kazanilan += agirlik * oran;
  if (neden) t.nedenler.push(neden);
};

/** Fiyat puanlama — daire ve portföy için ortak. */
const fiyatPuanla = (t: Toplayici, k: AramaKriterleri, fiyat: number | null) => {
  if (k.minFiyat === null && k.maxFiyat === null) return;
  if (fiyat === null || fiyat <= 0) {
    ekle(t, A.fiyat, 0.3, { metin: 'fiyat bilgisi yok', ton: 'notr' });
    return;
  }
  const oran = araliktanPuan(fiyat, k.minFiyat, k.maxFiyat, 0.25, 0.40);
  if (oran === 1) {
    ekle(t, A.fiyat, 1, { metin: 'Bütçe içinde', ton: 'iyi' });
  } else if (k.maxFiyat !== null && fiyat > k.maxFiyat) {
    ekle(t, A.fiyat, oran, { metin: `bütçenin %${yuzde((fiyat - k.maxFiyat) / k.maxFiyat)} üzerinde`, ton: 'uyari' });
  } else {
    ekle(t, A.fiyat, oran, { metin: 'bütçenin altında', ton: 'notr' });
  }
};

const m2Puanla = (t: Toplayici, k: AramaKriterleri, m2: number | null) => {
  if (k.minM2 === null && k.maxM2 === null) return;
  if (m2 === null || m2 <= 0) {
    ekle(t, A.m2, 0.3, { metin: 'm² bilgisi yok', ton: 'notr' });
    return;
  }
  const oran = araliktanPuan(m2, k.minM2, k.maxM2, 0.20, 0.20);
  ekle(t, A.m2, oran, oran === 1 ? { metin: `${m2} m²`, ton: 'iyi' } : { metin: `${m2} m² (aralık dışı)`, ton: 'uyari' });
};

const serbestPuanla = (t: Toplayici, k: AramaKriterleri, metinler: (string | null | undefined)[]) => {
  if (!k.serbestAnahtarKelimeler || k.serbestAnahtarKelimeler.length === 0) return;
  const havuz = trFold(metinler.filter(Boolean).join(' '));
  let isabet = 0;
  k.serbestAnahtarKelimeler.forEach(kelime => {
    if (havuz.includes(trFold(kelime))) {
      isabet++;
      t.nedenler.push({ metin: kelime, ton: 'notr' });
    }
  });
  ekle(t, A.serbest, isabet / k.serbestAnahtarKelimeler.length);
};

const daireSkorla = (d: EslestirmeDaire, k: AramaKriterleri): { skor: number; nedenler: EslesmeNedeni[] } => {
  const t: Toplayici = { kazanilan: 0, toplam: 0, nedenler: [] };

  fiyatPuanla(t, k, d.listeFiyati);

  // Oda tipi — daireTipi.tip serbest metin ("3+1 Dubleks")
  if (k.odaTipleri.length > 0) {
    const tip = odaTipiCikar(d.daireTipi?.tip);
    if (tip && k.odaTipleri.includes(tip)) {
      ekle(t, A.odaTipi, 1, { metin: tip, ton: 'iyi' });
    } else if (tip) {
      const [o1] = tip.split('+').map(Number);
      const yakin = k.odaTipleri.some(x => {
        const [o2, s2] = x.split('+').map(Number);
        const [, s1] = tip.split('+').map(Number);
        return Math.abs(o1 - o2) === 1 && s1 === s2;
      });
      ekle(t, A.odaTipi, yakin ? 0.4 : 0, yakin ? { metin: `yakın oda tipi (${tip})`, ton: 'uyari' } : undefined);
    } else {
      ekle(t, A.odaTipi, 0);
    }
  }

  const m2 = k.m2Turu === 'brut' ? (d.brutM2 ?? d.netM2) : (d.netM2 ?? d.brutM2 ?? null);
  m2Puanla(t, k, m2);

  if (k.projeAdi) {
    const eslesti = trIcerir(d.proje?.projeAdi, k.projeAdi);
    ekle(t, A.projeAdi, eslesti ? 1 : 0, eslesti ? { metin: d.proje!.projeAdi, ton: 'iyi' } : undefined);
  }

  // Gayrimenkul türü: Daireler daima konut envanteri
  if (k.gayrimenkulTuru) {
    const eslesti = k.gayrimenkulTuru === 'Konut';
    ekle(t, A.gayrimenkulTuru, eslesti ? 1 : 0);
  }

  if (k.yeniProje !== null) {
    const durum = d.proje?.projeDurumu || '';
    const devamEden = durum === 'Gelecek' || durum === 'Devam Ediyor';
    if (k.yeniProje) {
      ekle(t, A.yeniProje, devamEden ? 1 : 0.3,
        devamEden ? { metin: durum === 'Gelecek' ? 'Yeni proje' : 'Devam eden proje', ton: 'iyi' }
                  : { metin: 'tamamlanmış proje', ton: 'uyari' });
    } else {
      ekle(t, A.yeniProje, devamEden ? 0.3 : 1);
    }
  }

  if (k.sehir) {
    const eslesti = trIcerir(d.proje?.sehir, k.sehir);
    ekle(t, A.sehir, eslesti ? 1 : 0, eslesti ? { metin: d.proje!.sehir!, ton: 'iyi' } : undefined);
  }
  if (k.manzara) {
    const eslesti = trIcerir(d.manzara?.manzaraAdi, k.manzara);
    ekle(t, A.manzara, eslesti ? 1 : 0, eslesti ? { metin: `${d.manzara!.manzaraAdi} manzara`, ton: 'iyi' } : undefined);
  }
  if (k.cephe) {
    const eslesti = trIcerir(d.cephe?.cepheAdi, k.cephe);
    ekle(t, A.cephe, eslesti ? 1 : 0, eslesti ? { metin: d.cephe!.cepheAdi, ton: 'iyi' } : undefined);
  }
  if (k.katTercihi) {
    const eslesti = trIcerir(d.kat?.katAdi, k.katTercihi) || trIcerir(k.katTercihi, d.kat?.katAdi || '');
    ekle(t, A.kat, eslesti ? 1 : 0, eslesti ? { metin: d.kat!.katAdi, ton: 'iyi' } : undefined);
  }

  serbestPuanla(t, k, [d.daireNo, d.proje?.projeAdi, d.blok?.blokAdi, d.kat?.katAdi, d.cephe?.cepheAdi, d.manzara?.manzaraAdi, d.daireTipi?.tip]);

  const skor = t.toplam > 0 ? Math.round((100 * t.kazanilan) / t.toplam) : 0;
  return { skor, nedenler: t.nedenler };
};

const portfoySkorla = (p: EslestirmePortfoy, k: AramaKriterleri): { skor: number; nedenler: EslesmeNedeni[] } => {
  const t: Toplayici = { kazanilan: 0, toplam: 0, nedenler: [] };
  const metinHavuzu = [p.baslik, p.aciklama, ...(p.ozellikler || []).map(o => o.ozellikAdi)].filter(Boolean).join(' ');

  fiyatPuanla(t, k, p.fiyat || null);

  // Oda tipi: yapısal alan yok → metinsel çıkarım (kanıt zayıf olduğu için 0.8)
  if (k.odaTipleri.length > 0) {
    const bulunan = k.odaTipleri.find(tip => trFold(metinHavuzu).includes(trFold(tip)));
    if (bulunan) {
      const nerede = trIcerir(p.baslik, bulunan) ? 'başlıkta' : 'özelliklerde';
      ekle(t, A.odaTipi, 0.8, { metin: `${nerede} ${bulunan}`, ton: 'notr' });
    } else {
      ekle(t, A.odaTipi, 0.15);
    }
  }

  m2Puanla(t, k, p.m2 || null);

  if (k.gayrimenkulTuru) {
    const eslesti = trFold(p.tur) === trFold(k.gayrimenkulTuru);
    ekle(t, A.gayrimenkulTuru, eslesti ? 1 : 0, eslesti ? { metin: p.tur, ton: 'iyi' } : undefined);
  }

  if (k.sehir) {
    const eslesti = trIcerir(p.adres, k.sehir);
    ekle(t, A.sehir, eslesti ? 1 : 0, eslesti ? { metin: k.sehir, ton: 'iyi' } : undefined);
  }
  if (k.manzara) {
    const eslesti = trIcerir(metinHavuzu, k.manzara);
    ekle(t, A.manzara, eslesti ? 1 : 0, eslesti ? { metin: `${k.manzara} manzara`, ton: 'notr' } : undefined);
  }
  if (k.cephe) {
    const eslesti = trIcerir(metinHavuzu, k.cephe);
    ekle(t, A.cephe, eslesti ? 1 : 0, eslesti ? { metin: `${k.cephe} cephe`, ton: 'notr' } : undefined);
  }
  if (k.katTercihi) {
    const eslesti = trIcerir(metinHavuzu, k.katTercihi);
    ekle(t, A.kat, eslesti ? 1 : 0, eslesti ? { metin: k.katTercihi, ton: 'notr' } : undefined);
  }

  // yeniProje portföy için bilinemez → paydaya girmez, bilgi çipi eklenir
  if (k.yeniProje !== null) t.nedenler.push({ metin: 'yeni/sıfır bilgisi yok', ton: 'notr' });
  // projeAdi portföyde uygulanamaz → paydaya girmez

  serbestPuanla(t, k, [p.baslik, p.adres, p.aciklama, ...(p.ozellikler || []).map(o => o.ozellikAdi)]);

  let skor = t.toplam > 0 ? Math.round((100 * t.kazanilan) / t.toplam) : 0;

  // Örtük satın alma niyetinde kiralık portföyler geri plana atılır (elenmez)
  const satinAlmaNiyeti = k.islemTuru === null && (k.minFiyat !== null || k.maxFiyat !== null);
  if (satinAlmaNiyeti && trFold(p.durum) === 'kiralik') {
    skor = Math.round(skor * 0.35);
    t.nedenler.push({ metin: 'Kiralık', ton: 'uyari' });
  }

  return { skor, nedenler: t.nedenler };
};

export interface EslestirSecenekleri {
  mevcutDaireIDs?: Set<number | null> | Set<number>;
  mevcutPortfoyIDs?: Set<number | null> | Set<number>;
}

export const eslestir = (
  k: AramaKriterleri,
  daireler: EslestirmeDaire[],
  portfoyler: EslestirmePortfoy[],
  secenekler: EslestirSecenekleri = {}
): AramaSonucu[] => {
  const mevcutD = secenekler.mevcutDaireIDs ?? new Set<number>();
  const mevcutP = secenekler.mevcutPortfoyIDs ?? new Set<number>();

  const sonuclar: AramaSonucu[] = [];

  // Daireler firmanın satılık envanteri — açıkça kiralık aranıyorsa hiç gösterilmez
  if (k.islemTuru !== 'Kiralik') {
    daireler.forEach(d => {
      if ((mevcutD as Set<any>).has(d.daireID)) return;
      const { skor, nedenler } = daireSkorla(d, k);
      sonuclar.push({
        tur: 'Daire',
        id: d.daireID,
        baslik: `Daire ${d.daireNo}`,
        altBaslik: [d.proje?.projeAdi, d.blok?.blokAdi, d.kat?.katAdi, d.cephe?.cepheAdi].filter(Boolean).join(' • '),
        fiyat: d.listeFiyati ?? null,
        skor,
        tamEslesme: skor >= TAM_ESLESME_ESIGI,
        nedenler,
        daire: d,
      });
    });
  }

  portfoyler.forEach(p => {
    if ((mevcutP as Set<any>).has(p.portfoyID)) return;
    // Açık işlem türü belirtilmişse sert filtre ("Portfoy" durumundakiler her zaman geçer)
    if (k.islemTuru) {
      const durum = trFold(p.durum);
      if (durum !== 'portfoy' && durum !== trFold(k.islemTuru)) return;
    }
    const { skor, nedenler } = portfoySkorla(p, k);
    sonuclar.push({
      tur: 'Portfoy',
      id: p.portfoyID,
      baslik: p.baslik,
      altBaslik: [p.adres, p.tur].filter(Boolean).join(' · '),
      fiyat: p.fiyat ?? null,
      skor,
      tamEslesme: skor >= TAM_ESLESME_ESIGI,
      nedenler,
      portfoy: p,
    });
  });

  const hedefFiyat = k.minFiyat !== null && k.maxFiyat !== null
    ? (k.minFiyat + k.maxFiyat) / 2
    : (k.maxFiyat ?? k.minFiyat ?? 0);

  sonuclar.sort((a, b) => {
    if (b.skor !== a.skor) return b.skor - a.skor;
    if (hedefFiyat > 0) {
      const fa = Math.abs((a.fiyat ?? Number.MAX_SAFE_INTEGER) - hedefFiyat);
      const fb = Math.abs((b.fiyat ?? Number.MAX_SAFE_INTEGER) - hedefFiyat);
      if (fa !== fb) return fa - fb;
    }
    if (a.tur !== b.tur) return a.tur === 'Daire' ? -1 : 1;
    return a.id - b.id;
  });

  // Asla boş dönme: tam eşleşme azsa en yakınlarla tamamla
  const tamEslesenler = sonuclar.filter(s => s.tamEslesme);
  if (tamEslesenler.length >= 5) return tamEslesenler.slice(0, 20);

  const kalanlar = sonuclar.filter(s => !s.tamEslesme);
  const hepsiSifir = sonuclar.every(s => s.skor === 0);
  if (hepsiSifir) {
    return [...sonuclar]
      .sort((a, b) => (a.fiyat ?? Number.MAX_SAFE_INTEGER) - (b.fiyat ?? Number.MAX_SAFE_INTEGER))
      .slice(0, 15);
  }

  return [...tamEslesenler, ...kalanlar].slice(0, 15);
};
